import { useEffect, useRef, useState, type ClipboardEvent, type DragEvent, type KeyboardEvent } from "react";
import {
  ArrowUp, Camera, File, FileArchive, FileText, Film, Image as ImageIcon, Mic, Plus, Sheet as SheetIcon, Square, AudioLines, X,
  type LucideIcon,
} from "lucide-react";
import { useApp } from "../../context";
import type { ChatStore } from "../../state/chat";
import { useObserve } from "../../state/observable";
import { Menu, Spinner, useMenu } from "../../ui/components";
import { slashMatches } from "./slash";
import { Dictation, dictationSupported, type DictationState } from "./dictation";
import { Preferences } from "../../state/persistence";
import "./chat.css";

const MAX_FILE = 25 * 1024 * 1024;
const touch = typeof matchMedia !== "undefined" && matchMedia("(pointer: coarse)").matches;

/**
 * 输入框（对应 iOS 的 ComposerView）。
 * - 电脑：回车发送、Shift+回车换行；可以把文件拖进来、直接粘贴截图
 * - 手机：回车换行，点发送按钮发送
 * - “+”：照片 / 拍照 / 文件；输入 “/” 出命令联想
 */
export function Composer({ chat }: { chat: ChatStore }) {
  const app = useApp();
  useObserve(chat);
  const input = useRef<HTMLTextAreaElement>(null);
  const photoInput = useRef<HTMLInputElement>(null);
  const cameraInput = useRef<HTMLInputElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const plus = useMenu();
  const [dragging, setDragging] = useState(false);
  const [voice, setVoice] = useState<DictationState>("idle");
  const listening = voice !== "idle";
  const dictation = useRef<Dictation | null>(null);
  const showMic = dictationSupported() && Preferences.voiceEnabled;

  useEffect(() => {
    dictation.current = new Dictation(() => chat.draft, (t) => chat.setDraft(t), setVoice, (m) => app.showToast(m));
    // 切到后台就停止，已经识别的字保留
    const onHide = () => { if (document.visibilityState !== "visible") dictation.current?.stop(); };
    document.addEventListener("visibilitychange", onHide);
    return () => { document.removeEventListener("visibilitychange", onHide); dictation.current?.cancel(); };
  }, [chat]); // eslint-disable-line react-hooks/exhaustive-deps

  const draft = chat.draft;
  const trimmed = draft.trim();
  const hasAttachments = chat.images.length > 0 || chat.files.length > 0;
  const canSend = (trimmed.length > 0 || hasAttachments) && !chat.isSending;
  const showStop = chat.isRunning && !trimmed && !hasAttachments;
  const matches = draft.startsWith("/") && !/\s/.test(draft) ? slashMatches(draft, app.slashCommands) : [];

  // 自动长高：1～8 行
  useEffect(() => {
    const el = input.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 8 * 24 + 4)}px`;
  }, [draft]);

  // 电脑上打开对话就把光标放进输入框
  useEffect(() => {
    if (!touch) input.current?.focus({ preventScroll: true });
  }, [chat]);

  const submit = () => {
    if (listening) dictation.current?.stop();
    if (!canSend) return;
    const text = chat.draft;
    chat.setDraft("");
    if (touch) input.current?.blur();
    void chat.send(text);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== "Enter" || e.nativeEvent.isComposing || e.keyCode === 229) return;
    if (matches.length && !e.shiftKey) {
      e.preventDefault();
      chat.setDraft(matches[0].command + " ");
      return;
    }
    if (!touch && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const importFiles = async (files: FileList | File[]) => {
    for (const f of Array.from(files)) {
      if (f.size > MAX_FILE) { app.showToast(`${f.name} 超过 25 MB，太大了`); continue; }
      if (f.type.startsWith("image/") && !f.type.includes("svg")) await attachImage(chat, f);
      else await chat.attachFile(new Uint8Array(await f.arrayBuffer()), f.name, f.type || "application/octet-stream");
    }
  };

  const onPaste = (e: ClipboardEvent) => {
    const files = Array.from(e.clipboardData.files);
    if (files.length) { e.preventDefault(); void importFiles(files); }
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files.length) void importFiles(e.dataTransfer.files);
  };

  return (
    <div
      className={`composer-wrap${dragging ? " dragging" : ""}`}
      onDragOver={(e) => { if (e.dataTransfer.types.includes("Files")) { e.preventDefault(); setDragging(true); } }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
    >
      {matches.length > 0 && (
        <div className="slash-list scroll">
          {matches.map((m) => {
            const Icon = m.icon;
            return (
              <button key={m.command} className="slash-row" onMouseDown={(e) => e.preventDefault()} onClick={() => { chat.setDraft(m.command + " "); input.current?.focus(); }}>
                <Icon size={15} className="t-brand" />
                <span className="col" style={{ minWidth: 0 }}>
                  <span className="t-subheadline medium nowrap">{m.title}</span>
                  <span className="t-caption2 mono t-3">{m.command}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}
      <div className="composer">
        {hasAttachments && (
          <div className="attachment-strip">
            {chat.files.map((f) => {
              const Icon = fileIcon(f.name);
              return (
                <div key={f.id} className="attachment-file">
                  <Icon size={20} className="t-brand" />
                  <span className="col" style={{ minWidth: 0, flex: 1 }}>
                    <span className="t-caption medium nowrap">{f.name}</span>
                    <span className="t-caption2 t-3">{formatBytes(f.size)}</span>
                  </span>
                  {f.uploading && <Spinner size={14} />}
                  <button className="attachment-remove" aria-label="移除" onClick={() => chat.removeFile(f)}><X size={11} strokeWidth={3} /></button>
                </div>
              );
            })}
            {chat.images.map((img) => (
              <div key={img.id} className="attachment-image">
                <img src={img.thumbnail} alt="" />
                {img.uploading && <span className="attachment-uploading"><span className="spinner light" /></span>}
                <button className="attachment-remove" aria-label="移除" onClick={() => void chat.removeImage(img)}><X size={11} strokeWidth={3} /></button>
              </div>
            ))}
          </div>
        )}
        <textarea
          ref={input}
          rows={1}
          value={draft}
          placeholder={voice === "starting" ? "正在打开麦克风…" : voice === "listening" ? "正在听…" : `问 ${app.botTitle} 任何问题`}
          onChange={(e) => chat.setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          enterKeyHint={touch ? "enter" : "send"}
          aria-label="输入消息"
        />
        <div className="composer-bar">
          <button className="composer-plus" aria-label="添加照片或文件" onClick={(e) => plus.show(e.currentTarget)}>
            <Plus size={20} strokeWidth={2.4} />
          </button>
          <span className="spacer" />
          {showMic && (
            <button className={`mic-button${listening ? " on" : ""}`} aria-label={listening ? "结束语音输入" : "语音输入"}
              onClick={() => (listening ? dictation.current?.stop() : void dictation.current?.start())}>
              {voice === "listening" ? <span className="mic-bars"><i /><i /><i /></span> : voice === "starting" ? <span className="spinner light" style={{ width: 14, height: 14 }} /> : <Mic size={19} />}
            </button>
          )}
          {showStop ? (
            <button className="send-button on" aria-label="停止" onClick={() => void chat.stop()}>
              <Square size={13} fill="currentColor" strokeWidth={0} />
            </button>
          ) : (
            <button className={`send-button${canSend ? " on" : ""}`} aria-label="发送" disabled={!canSend} onClick={submit}>
              <ArrowUp size={19} strokeWidth={2.8} />
            </button>
          )}
        </div>
      </div>
      <input ref={photoInput} type="file" accept="image/*" multiple hidden onChange={(e) => { if (e.target.files) void importFiles(e.target.files); e.target.value = ""; }} />
      <input ref={cameraInput} type="file" accept="image/*" capture="environment" hidden onChange={(e) => { if (e.target.files) void importFiles(e.target.files); e.target.value = ""; }} />
      <input ref={fileInput} type="file" multiple hidden onChange={(e) => { if (e.target.files) void importFiles(e.target.files); e.target.value = ""; }} />
      <Menu
        open={plus.open}
        anchor={plus.anchor}
        onClose={plus.close}
        align="start"
        width={180}
        items={[
          { label: "照片", icon: <ImageIcon size={16} />, onSelect: () => photoInput.current?.click() },
          ...(touch ? [{ label: "拍照", icon: <Camera size={16} />, onSelect: () => cameraInput.current?.click() }] : []),
          { label: "文件", icon: <File size={16} />, onSelect: () => fileInput.current?.click() },
        ]}
      />
    </div>
  );
}

/** 图片先压到最长边 2048、JPEG 0.85 再上传（和 iOS 一样），动图和小图原样发 */
async function attachImage(chat: ChatStore, file: File): Promise<void> {
  const preview = URL.createObjectURL(file);
  let data = new Uint8Array(await file.arrayBuffer());
  let name = file.name || `photo-${Math.floor(Date.now() / 1000)}.jpg`;
  if (file.type !== "image/gif" && file.size > 600 * 1024) {
    try {
      const bitmap = await createImageBitmap(file);
      const scale = Math.min(1, 2048 / Math.max(bitmap.width, bitmap.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(bitmap.width * scale);
      canvas.height = Math.round(bitmap.height * scale);
      canvas.getContext("2d")!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, "image/jpeg", 0.85));
      if (blob && blob.size < file.size) {
        data = new Uint8Array(await blob.arrayBuffer());
        name = name.replace(/\.[^.]+$/, "") + ".jpg";
      }
    } catch { /* 解不开就原样发 */ }
  }
  await chat.attachImage(data, preview, name);
}

function fileIcon(name: string): LucideIcon {
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  if (["pdf", "doc", "docx", "pages", "rtf", "txt", "md"].includes(ext)) return FileText;
  if (["xls", "xlsx", "csv", "numbers"].includes(ext)) return SheetIcon;
  if (["zip", "rar", "7z", "gz"].includes(ext)) return FileArchive;
  if (["mp3", "m4a", "wav"].includes(ext)) return AudioLines;
  if (["mp4", "mov"].includes(ext)) return Film;
  return File;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} 字节`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
